import type {ColumnsType} from "antd/es/table";
import {
  StyledDescriptionText,
  StyledDatePicker,
  StyledNoUpcomingCallBox,
  StyledTransactionTypeContainer,
  StyledTransactionsTableWrapper,
  StyledUpcomingCallsWrapper,
} from "./transactions.style";
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Modal,
  Radio,
  Row,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  message,
} from "antd";
import {
  DownloadOutlined,
  FilterOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import moment from "moment";
import {TransactionDetailsModal} from "./details/transaction-details-modal";
import {
  useExportCoachTransactionsCSV,
  useGetCoachTransactions,
  useGetCoachForeignTransactions,
  BaseCoachTransactionApiResponse,
  ForeignCurrencyTransactionApiResponse,
} from "../../react-query-hooks/transactions";
import {
  Transaction,
  useGetLocalTransactions,
  useGetForeignTransactions,
  useFilterLocalTransactions,
  useDomainFilterForeignTransactions,
  toLegacyLocalTransaction,
  toLegacyForeignTransaction,
} from "../../features/transactions";
import {useEffect, useState} from "react";
import {Controller, useForm} from "react-hook-form";
import {CustomInputComponent} from "../input/input-component";
import type {RangePickerProps} from "antd/es/date-picker";
import {StyledModalContentWrapper} from "../calls/upcoming-calls/call-card/call-card.style";
import {FormFieldError} from "../../design-system/form-field";
import {useSearchParams} from "react-router-dom";

interface DataType {
  key: string;
  offering: string;
  customer: string;
  amount: string;
  email: string;
  reference: string;
  earnings: string;
  date: string;
  status?: "Active" | "Completed" | "Paid" | "Pending Conversion";
}

interface ForeignCurrencyDataType {
  key: string;
  offering: string;
  customer: string;
  amount: string;
  currency: string;
  convertedPrice: string;
  exchangeRate: string;
  email: string;
  reference: string;
  date: string;
  status?: "Active" | "Completed" | "Paid" | "Pending Conversion";
}

type TransactionsTableFilterFormValues = {
  searchParams?: string;
  startDate: string;
  endDate: string;
};
export type ExportTransactionsCsvFilterFormValues = {
  startDate: string;
  endDate: string;
};

const handleOnRow = (record: any) => {
  return {
    onClick: () => {}, // click row
  };
};

export function TransactionsTable() {
  const [searchParams, setSearchParams] = useSearchParams();

  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const pageSize = 10;
  const urlTransactionType = searchParams.get("type") as "local" | "foreign" | null;

  const [transactionType, setTransactionType] = useState<"local" | "foreign">(
    urlTransactionType || "local"
  );

  useEffect(() => {
    if (urlTransactionType && urlTransactionType !== transactionType) {
      setTransactionType(urlTransactionType);
    }
  }, [urlTransactionType]);

  useEffect(() => {
    if (urlTransactionType !== transactionType) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("type", transactionType);
      newParams.set("page", "1");
      newParams.delete("limit");
      setSearchParams(newParams);
    }
  }, [transactionType, urlTransactionType, searchParams, setSearchParams]);

  const getTransactionsQuery = useGetCoachTransactions(
    transactionType === "local",
    currentPage,
    pageSize
  );
  const getForeignTransactionsQuery = useGetCoachForeignTransactions(
    transactionType === "foreign",
    currentPage,
    pageSize
  );
  const localTransactionsDomainQuery = useGetLocalTransactions(
    transactionType === "local",
    currentPage,
    pageSize
  );
  const foreignTransactionsDomainQuery = useGetForeignTransactions(
    transactionType === "foreign",
    currentPage,
    pageSize
  );

  const exportCsvQueryMutation = useExportCoachTransactionsCSV();
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [transactions, setTransactions] = useState<BaseCoachTransactionApiResponse[]>([]);
  const [foreignTransactions, setForeignTransactions] = useState<
    ForeignCurrencyTransactionApiResponse[]
  >([]);
  const [transactionEntities, setTransactionEntities] = useState<Transaction[]>([]);
  const [foreignTransactionEntities, setForeignTransactionEntities] = useState<
    Transaction[]
  >([]);
  const filterLocalTransactionsDomain = useFilterLocalTransactions();
  const filterForeignTransactionsDomain = useDomainFilterForeignTransactions();
  const [isExportCsvModalOpen, setIsExportCsvModalOpen] = useState(false);
  const [hasFiltersApplied, setHasFiltersApplied] = useState(false);
  const [appliedFilters, setAppliedFilters] =
    useState<TransactionsTableFilterFormValues | null>(null);
  const [filteredMeta, setFilteredMeta] = useState<{
    local?: {total_pages: number; per_page: number};
    foreign?: {total_pages: number; per_page: number};
  }>({});

  useEffect(() => {
    if (!hasFiltersApplied) {
      setTransactions(getTransactionsQuery.data?.data?.localTransactions ?? []);
    }
  }, [getTransactionsQuery.data?.data?.localTransactions, hasFiltersApplied]);

  useEffect(() => {
    if (!hasFiltersApplied) {
      setForeignTransactions(
        getForeignTransactionsQuery.data?.data?.fxTransactions ?? []
      );
    }
  }, [getForeignTransactionsQuery.data?.data?.fxTransactions, hasFiltersApplied]);

  useEffect(() => {
    if (!hasFiltersApplied) {
      setTransactionEntities(localTransactionsDomainQuery.data?.data ?? []);
    }
  }, [localTransactionsDomainQuery.data?.data, hasFiltersApplied]);

  useEffect(() => {
    if (!hasFiltersApplied) {
      setForeignTransactionEntities(foreignTransactionsDomainQuery.data?.data ?? []);
    }
  }, [foreignTransactionsDomainQuery.data?.data, hasFiltersApplied]);

  useEffect(() => {
    setAppliedFilters(null);
    setHasFiltersApplied(false);
    setFilteredMeta({});
  }, [transactionType]);

  const updatePaginationParams = (page: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", page.toString());
    newParams.delete("limit");
    setSearchParams(newParams);
  };

  const {
    handleSubmit,
    formState: {errors},
    watch,
    control,
    reset,
  } = useForm<TransactionsTableFilterFormValues>({
    defaultValues: {
      searchParams: "",
      startDate: "",
      endDate: "",
    },
  });
  const exportCsvFormMethods = useForm<ExportTransactionsCsvFilterFormValues>({
    defaultValues: {
      startDate: moment().subtract(30, "days").format("YYYY-MM-DD"),
      endDate: moment().format("YYYY-MM-DD"),
    },
  });
  // console.log({mom: moment("Wed Mar 19 2025")});
  const disabledDate: RangePickerProps["disabledDate"] = (current) => {
    // Can not select days after today and today
    return current && current > moment();
  };
  const handleCancel = () => {
    setIsModalOpen(false);
    setSelectedTransaction(null);
  };

  const showModal = (reference: string) => {
    const entities =
      transactionType === "local" ? transactionEntities : foreignTransactionEntities;
    const transaction = entities.find((t) => t.reference === reference);
    if (transaction) {
      setSelectedTransaction(transaction);
    }
    setIsModalOpen(true);
  };

  const onSubmit = async (values: TransactionsTableFilterFormValues) => {
    updatePaginationParams(1);
    setAppliedFilters(values);

    const domainPayload = {
      startDate: values.startDate,
      endDate: values.endDate,
      searchParams: values.searchParams,
      page: 1,
      limit: 10,
    };

    if (transactionType === "local") {
      filterLocalTransactionsDomain.mutate(domainPayload, {
        onSuccess: (result) => {
          const legacyTransactions: BaseCoachTransactionApiResponse[] = result.data.map(
            toLegacyLocalTransaction
          );
          setTransactions(legacyTransactions);
          setTransactionEntities(result.data);
          setFilteredMeta((prev) => ({
            ...prev,
            local: {
              total_pages: result.meta.totalPages,
              per_page: result.meta.perPage,
            },
          }));
          setHasFiltersApplied(true);
        },
      });
    } else {
      filterForeignTransactionsDomain.mutate(domainPayload, {
        onSuccess: (result) => {
          const legacyTransactions: ForeignCurrencyTransactionApiResponse[] =
            result.data.map(toLegacyForeignTransaction);
          setForeignTransactions(legacyTransactions);
          setForeignTransactionEntities(result.data);
          setFilteredMeta((prev) => ({
            ...prev,
            foreign: {
              total_pages: result.meta.totalPages,
              per_page: result.meta.perPage,
            },
          }));
          setHasFiltersApplied(true);
        },
      });
    }
  };

  const columns: ColumnsType<DataType> = [
    {
      title: "Offering",
      dataIndex: "offering",
      fixed: "left",
      width: "15rem",
      key: "offering",
      render: (text) => (
        <>
          <StyledDescriptionText>{text}</StyledDescriptionText>
        </>
      ),
    },
    {
      title: "Customer",
      dataIndex: "customer",
      width: "15rem",
      key: "customer",
      render: (text) => (
        <>
          <StyledDescriptionText>{text}</StyledDescriptionText>
        </>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      width: "15rem",
      key: "amount",
      render: (text, record) => (
        <>
          <StyledDescriptionText>{text}</StyledDescriptionText>
        </>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      width: "15rem",
      key: "email",
      render: (text) => (
        <>
          <StyledDescriptionText>{text}</StyledDescriptionText>
        </>
      ),
    },
    {
      title: "Reference",
      dataIndex: "reference",
      width: "15rem",
      key: "reference",
      render: (text) => (
        <>
          <StyledDescriptionText>{text}</StyledDescriptionText>
        </>
      ),
    },
    // {
    //   title: "Earnings",
    //   dataIndex: "earnings",
    //   width: "12rem",
    //   key: "earnings",
    //   render: (text) => (
    //     <>
    //       <StyledDescriptionText>{text}</StyledDescriptionText>
    //     </>
    //   ),
    // },
    {
      title: "Date",
      dataIndex: "date",
      width: "15rem",
      key: "date",
      render: (text) => (
        <>
          <StyledDescriptionText>
            {moment(text).format("ddd, MMM DD YYYY")}
          </StyledDescriptionText>
        </>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: "15rem",
      key: "status",
      render: (text: string | undefined) => (
        <>
          {!text ? (
            <Tag color={"default"}>N/A</Tag>
          ) : text.toLowerCase() === "pending conversion" ? (
            <Tooltip
              placement="right"
              title="Pending conversions typically takes 3 to 4 days before they are added to your wallet"
            >
              <div style={{display: "flex", alignItems: "center"}}>
                <Tag color="warning">Pending conversion</Tag>

                <Button
                  style={{color: "#FAAD14"}}
                  icon={<QuestionCircleOutlined />}
                  size="small"
                  type="text"
                />
              </div>
            </Tooltip>
          ) : (
            <Tag color={"green"}>{text}</Tag>
          )}
        </>
      ),
    },
    {
      title: "",
      dataIndex: "action",
      width: "15rem",
      key: "action",
      render: (_, record) => (
        <Button size="small" type="link" onClick={() => showModal(record.reference)}>
          <span style={{textDecoration: "underline"}}>View details</span>
        </Button>
      ),
    },
  ];

  const foreignCurrencyColumns: ColumnsType<ForeignCurrencyDataType> = [
    {
      title: "Offering",
      dataIndex: "offering",
      fixed: "left",
      width: "15rem",
      key: "offering",
      render: (text) => (
        <>
          <StyledDescriptionText>{text}</StyledDescriptionText>
        </>
      ),
    },
    {
      title: "Customer",
      dataIndex: "customer",
      width: "15rem",
      key: "customer",
      render: (text) => (
        <>
          <StyledDescriptionText>{text}</StyledDescriptionText>
        </>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      width: "15rem",
      key: "amount",
      render: (text, record) => (
        <>
          <StyledDescriptionText>
            {record.currency ? `${record.currency} ${text}` : text}
          </StyledDescriptionText>
        </>
      ),
    },
    {
      title: "Converted price",
      dataIndex: "convertedPrice",
      width: "15rem",
      key: "convertedPrice",
      render: (text) => (
        <>
          <StyledDescriptionText>{text}</StyledDescriptionText>
        </>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: "15rem",
      key: "status",
      render: (text: string | undefined) => (
        <>
          {!text ? (
            <Tag color={"default"}>N/A</Tag>
          ) : text.toLowerCase() === "pending conversion" ? (
            <Tooltip
              placement="right"
              title="Pending conversions typically takes 3 to 4 days before they are added to your wallet"
            >
              <div style={{display: "flex", alignItems: "center"}}>
                <Tag color="warning">Pending conversion</Tag>

                <Button
                  style={{color: "#FAAD14"}}
                  icon={<QuestionCircleOutlined />}
                  size="small"
                  type="text"
                />
              </div>
            </Tooltip>
          ) : (
            <Tag color={"green"}>{text}</Tag>
          )}
        </>
      ),
    },
    {
      title: "Exchange rate",
      dataIndex: "exchangeRate",
      width: "15rem",
      key: "exchangeRate",
      render: (text) => (
        <>
          <StyledDescriptionText style={{textAlign: "center"}}>
            {text}
          </StyledDescriptionText>
        </>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      width: "15rem",
      key: "email",
      render: (text) => (
        <>
          <StyledDescriptionText>{text}</StyledDescriptionText>
        </>
      ),
    },
    {
      title: "Reference",
      dataIndex: "reference",
      width: "15rem",
      key: "reference",
      render: (text) => (
        <>
          <StyledDescriptionText>{text}</StyledDescriptionText>
        </>
      ),
    },
    {
      title: "Date",
      dataIndex: "date",
      width: "15rem",
      key: "date",
      render: (text) => (
        <>
          <StyledDescriptionText>
            {moment(text).format("ddd, MMM DD YYYY")}
          </StyledDescriptionText>
        </>
      ),
    },
    {
      title: "",
      dataIndex: "action",
      width: "15rem",
      key: "action",
      render: (_, record) => (
        <Button size="small" type="link" onClick={() => showModal(record.reference)}>
          <span style={{textDecoration: "underline"}}>View details</span>
        </Button>
      ),
    },
  ];

  if (
    (transactionType === "local" && getTransactionsQuery.isLoading) ||
    (transactionType === "foreign" && getForeignTransactionsQuery.isLoading)
  ) {
    return (
      <div>
        <Spin />
      </div>
    );
  }
  if (
    (transactionType === "local" && getTransactionsQuery.isError) ||
    (transactionType === "foreign" && getForeignTransactionsQuery.isError)
  ) {
    const error =
      transactionType === "local"
        ? getTransactionsQuery.error
        : getForeignTransactionsQuery.error;
    return <Alert message="Error" description={error?.message} type="error" showIcon />;
  }
  const transactionsData: DataType[] = transactions.map((t) => ({
    amount: `${t.currency} ${t.amount.toLocaleString()}`,
    customer: t.full_name,
    date: new Date(t.payment_date).toDateString(),
    earnings: `${t.currency} ${t.amount.toLocaleString()}`,
    email: t.email,
    key: `${t.reference} ${t.payment_date}`,
    offering: t.service_name || "N/A",
    reference: t.reference,
    status: t.status_name,
  }));

  const foreignTransactionsData: ForeignCurrencyDataType[] = foreignTransactions.map(
    (t) => ({
      amount: t.price,
      currency: t.currency,
      convertedPrice: t.converted_price,
      exchangeRate: t.exchange_rate,
      customer: t.customer_name,
      date: new Date(t.payment_date).toDateString(),
      email: t.customer_email,
      key: `${t.reference} ${t.payment_date}`,
      offering: t.service_name,
      reference: t.reference,
      status: t.status_name,
    })
  );

  function onExportCsvFilterSubmit(values: ExportTransactionsCsvFilterFormValues) {
    // console.log({csvvalues: values});
    const payload = {
      startDate: Boolean(values.startDate)
        ? values.startDate
        : moment().subtract(30, "days").format("YYYY-MM-DD"),
      endDate: Boolean(values.endDate) ? values.endDate : moment().format("YYYY-MM-DD"),
      transactionType: transactionType,
    };
    exportCsvQueryMutation.mutate(payload, {
      onSuccess: (data) => {
        message.success("File ready to be downloaded!");
        const url = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `Coachli ${
            transactionType === "local" ? "Local" : "Foreign Currency"
          } Transactions.csv`
        );
        document.body.appendChild(link);
        link.click();
        exportCsvFormMethods.reset();
        setIsExportCsvModalOpen(false);
      },
    });
  }

  return (
    <div>
      <div
        style={{
          padding: "2rem",
          backgroundColor: "white",
          marginTop: "3rem",
          borderRadius: "4px",
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Space
            size={16}
            direction="vertical"
            style={{display: "flex", alignContent: "center"}}
          >
            <Row gutter={[20, 15]}>
              <Col span={24} sm={12} md={10}>
                <CustomInputComponent
                  id="search"
                  name="searchParams"
                  label="Search"
                  placeholder="Search by offering title or customer name"
                  control={control}
                />
              </Col>
              <Col
                span={24}
                md={3}
                sm={12}
                style={{display: "flex", flexDirection: "column"}}
              >
                <label style={{marginBottom: "8px"}}>Start date</label>
                <Controller
                  control={control}
                  name="startDate"
                  render={({
                    field: {onChange, value, onBlur, ref},
                    fieldState: {error},
                  }) => (
                    <StyledDatePicker
                      className="date-picker-small"
                      disabledDate={disabledDate}
                      size="large"
                      style={{width: "100%", height: "100%"}}
                      onChange={(date, dateString) => onChange(dateString)}
                      status={error?.message ? "error" : undefined}
                      value={!value ? undefined : moment(value)}
                      onBlur={onBlur}
                      ref={ref}
                    />
                  )}
                />
              </Col>
              <Col
                span={24}
                md={3}
                sm={12}
                style={{display: "flex", flexDirection: "column"}}
              >
                <label style={{marginBottom: "8px"}}>End date</label>
                <Controller
                  control={control}
                  name="endDate"
                  render={({
                    field: {onChange, value, onBlur, ref},
                    fieldState: {error},
                  }) => (
                    <StyledDatePicker
                      className="date-picker-small"
                      disabledDate={disabledDate}
                      size="large"
                      style={{width: "100%", height: "100%"}}
                      onChange={(date, dateString) => onChange(dateString)}
                      status={error?.message ? "error" : undefined}
                      value={!value ? undefined : moment(value)}
                      onBlur={onBlur}
                      ref={ref}
                    />
                  )}
                />
              </Col>
              <Col span={24} md={4} sm={12} style={{display: "flex", alignItems: "end"}}>
                <Button
                  disabled={
                    Object.keys(errors).length > 0 || Object.values(watch()).length < 1
                  }
                  block
                  loading={
                    transactionType === "local"
                      ? filterLocalTransactionsDomain.isLoading
                      : filterForeignTransactionsDomain.isLoading
                  }
                  size="small"
                  type="primary"
                  ghost
                  htmlType="submit"
                  icon={<FilterOutlined />}
                >
                  Apply Filter
                </Button>
              </Col>
              <Col span={24} md={3} sm={12} style={{display: "flex", alignItems: "end"}}>
                <Button
                  block
                  loading={
                    transactionType === "local"
                      ? getTransactionsQuery.isLoading
                      : getForeignTransactionsQuery.isLoading
                  }
                  size="small"
                  type="primary"
                  ghost
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    reset();
                    updatePaginationParams(1);
                    setAppliedFilters(null);
                    setFilteredMeta({});
                    if (transactionType === "local") {
                      setTransactions(
                        getTransactionsQuery.data?.data?.localTransactions ?? []
                      );
                    } else {
                      setForeignTransactions(
                        getForeignTransactionsQuery.data?.data?.fxTransactions ?? []
                      );
                    }
                    setHasFiltersApplied(false);
                  }}
                >
                  Reset
                </Button>
              </Col>
            </Row>
          </Space>
        </form>
        <StyledTransactionTypeContainer>
          <div>
            <p style={{fontWeight: 600, marginBottom: "1rem", fontSize: "1.4rem"}}>
              Transaction type
            </p>
            <Radio.Group
              value={transactionType}
              onChange={(e) => setTransactionType(e.target.value)}
            >
              <Radio value="local">Local transactions</Radio>
              <Radio value="foreign">Foreign currency transactions</Radio>
            </Radio.Group>
          </div>
          <Button
            onClick={() => setIsExportCsvModalOpen(true)}
            disabled={
              transactionType === "local"
                ? transactionsData.length < 1
                : foreignTransactionsData.length < 1
            }
            icon={<DownloadOutlined style={{fontSize: "1.6rem"}} />}
            type="text"
            size="small"
            loading={exportCsvQueryMutation.isLoading}
          >
            <span style={{textDecoration: "underline"}}>Export CSV</span>
          </Button>
        </StyledTransactionTypeContainer>
      </div>
      <StyledTransactionsTableWrapper style={{marginTop: "1.6rem"}}>
        {transactionType === "local" ? (
          <Spin
            spinning={getTransactionsQuery.isFetching || getTransactionsQuery.isLoading}
          >
            {transactionsData.length < 1 ? (
              <StyledUpcomingCallsWrapper style={{paddingTop: 0}}>
                <StyledNoUpcomingCallBox>
                  <p className="info-text">
                    {hasFiltersApplied
                      ? "No transactions found matching your filters"
                      : "You do not have any transactions"}
                  </p>
                </StyledNoUpcomingCallBox>
              </StyledUpcomingCallsWrapper>
            ) : (
              <Table
                onRow={handleOnRow}
                columns={columns}
                dataSource={transactionsData}
                scroll={{x: 100}}
                loading={
                  getTransactionsQuery.isFetching || getTransactionsQuery.isLoading
                }
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  total:
                    hasFiltersApplied && filteredMeta.local
                      ? filteredMeta.local.total_pages * filteredMeta.local.per_page
                      : getTransactionsQuery.data?.meta
                      ? getTransactionsQuery.data.meta.total_pages *
                        getTransactionsQuery.data.meta.per_page
                      : 0,
                  position: ["bottomLeft"],
                  onChange: (page) => {
                    if (hasFiltersApplied && appliedFilters) {
                      updatePaginationParams(page);

                      const domainPayload = {
                        startDate: appliedFilters.startDate,
                        endDate: appliedFilters.endDate,
                        searchParams: appliedFilters.searchParams,
                        page: page,
                        limit: 10,
                      };

                      filterLocalTransactionsDomain.mutate(domainPayload, {
                        onSuccess: (result) => {
                          const legacyTransactions: BaseCoachTransactionApiResponse[] =
                            result.data.map(toLegacyLocalTransaction);
                          setTransactions(legacyTransactions);
                          setTransactionEntities(result.data);
                          setFilteredMeta((prev) => ({
                            ...prev,
                            local: {
                              total_pages: result.meta.totalPages,
                              per_page: result.meta.perPage,
                            },
                          }));
                        },
                      });
                    } else {
                      updatePaginationParams(page);
                    }
                  },
                  itemRender: (page, type, originalElement) => {
                    if (type === "prev") {
                      return <Button size="small">Previous</Button>;
                    }
                    if (type === "next") {
                      return <Button size="small">Next</Button>;
                    }
                    if (type === "page") {
                      const isActive = page === currentPage;
                      return (
                        <Button
                          size="small"
                          style={
                            isActive
                              ? {
                                  backgroundColor: "#eae6f3",
                                }
                              : {}
                          }
                        >
                          {page}
                        </Button>
                      );
                    }
                    return originalElement;
                  },
                }}
              />
            )}
          </Spin>
        ) : (
          <Spin
            spinning={
              getForeignTransactionsQuery.isFetching ||
              getForeignTransactionsQuery.isLoading
            }
          >
            {foreignTransactionsData.length < 1 ? (
              <StyledUpcomingCallsWrapper style={{paddingTop: 0}}>
                <StyledNoUpcomingCallBox>
                  <p className="info-text">
                    {hasFiltersApplied
                      ? "No foreign currency transactions found matching your filters"
                      : "You do not have any foreign currency transactions"}
                  </p>
                </StyledNoUpcomingCallBox>
              </StyledUpcomingCallsWrapper>
            ) : (
              <Table
                onRow={handleOnRow}
                columns={foreignCurrencyColumns}
                dataSource={foreignTransactionsData}
                scroll={{x: 100}}
                loading={
                  getForeignTransactionsQuery.isFetching ||
                  getForeignTransactionsQuery.isLoading
                }
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  total:
                    hasFiltersApplied && filteredMeta.foreign
                      ? filteredMeta.foreign.total_pages * filteredMeta.foreign.per_page
                      : getForeignTransactionsQuery.data?.meta
                      ? getForeignTransactionsQuery.data.meta.total_pages *
                        getForeignTransactionsQuery.data.meta.per_page
                      : 0,
                  position: ["bottomLeft"],
                  onChange: (page) => {
                    if (hasFiltersApplied && appliedFilters) {
                      updatePaginationParams(page);

                      const domainPayload = {
                        startDate: appliedFilters.startDate,
                        endDate: appliedFilters.endDate,
                        searchParams: appliedFilters.searchParams,
                        page: page,
                        limit: 10,
                      };

                      filterForeignTransactionsDomain.mutate(domainPayload, {
                        onSuccess: (result) => {
                          const legacyTransactions: ForeignCurrencyTransactionApiResponse[] =
                            result.data.map(toLegacyForeignTransaction);
                          setForeignTransactions(legacyTransactions);
                          setForeignTransactionEntities(result.data);
                          setFilteredMeta((prev) => ({
                            ...prev,
                            foreign: {
                              total_pages: result.meta.totalPages,
                              per_page: result.meta.perPage,
                            },
                          }));
                        },
                      });
                    } else {
                      updatePaginationParams(page);
                    }
                  },
                  itemRender: (page, type, originalElement) => {
                    if (type === "prev") {
                      return <Button size="small">Previous</Button>;
                    }
                    if (type === "next") {
                      return <Button size="small">Next</Button>;
                    }
                    if (type === "page") {
                      const isActive = page === currentPage;
                      return (
                        <Button
                          size="small"
                          style={
                            isActive
                              ? {
                                  backgroundColor: "#eae6f3",
                                }
                              : {}
                          }
                        >
                          {page}
                        </Button>
                      );
                    }
                    return originalElement;
                  },
                }}
              />
            )}
          </Spin>
        )}
      </StyledTransactionsTableWrapper>
      <TransactionDetailsModal
        transaction={selectedTransaction}
        visibleState={isModalOpen}
        okFunc={handleCancel}
        cancelFunc={handleCancel}
      />
      <Modal
        title="Filter export"
        centered
        visible={isExportCsvModalOpen}
        onOk={() => setIsExportCsvModalOpen(false)}
        onCancel={() => setIsExportCsvModalOpen(false)}
        footer={
          <div>
            <Button
              size="small"
              onClick={() => {
                exportCsvFormMethods.reset();
                setIsExportCsvModalOpen(false);
              }}
              type="ghost"
              disabled={exportCsvQueryMutation.isLoading}
            >
              Cancel
            </Button>
            <Button
              size="small"
              onClick={exportCsvFormMethods.handleSubmit(onExportCsvFilterSubmit)}
              type="primary"
              loading={exportCsvQueryMutation.isLoading}
            >
              Export CSV
            </Button>
          </div>
        }
      >
        <StyledModalContentWrapper>
          <Space direction="vertical" size={8} style={{display: "flex"}}>
            <p style={{fontSize: "1.4rem", fontWeight: 600}}>Date range</p>
            <div>
              <form>
                <Row gutter={[24, 24]}>
                  <Col span={24} md={12}>
                    <Controller
                      control={exportCsvFormMethods.control}
                      name={"startDate"}
                      render={({
                        field: {onChange, value, ref, onBlur},
                        fieldState: {error},
                      }) => (
                        <Space size={16} direction="vertical" style={{display: "flex"}}>
                          <Space size={8} direction="vertical" style={{display: "flex"}}>
                            <p>Start date</p>
                            <DatePicker
                              disabledDate={disabledDate}
                              size="large"
                              style={{width: "100%"}}
                              onChange={(date, dateString) => onChange(dateString)}
                              status={error?.message ? "error" : undefined}
                              value={!value ? undefined : moment(value)}
                              onBlur={onBlur}
                              ref={ref}
                            />
                          </Space>
                          <FormFieldError errorMessage={error?.message} />
                        </Space>
                      )}
                    />
                  </Col>
                  <Col span={24} md={12}>
                    <Controller
                      control={exportCsvFormMethods.control}
                      name={"endDate"}
                      render={({
                        field: {onChange, value, ref, onBlur},
                        fieldState: {error},
                      }) => (
                        <Space size={16} direction="vertical" style={{display: "flex"}}>
                          <Space size={8} direction="vertical" style={{display: "flex"}}>
                            <p>End date</p>
                            <DatePicker
                              disabledDate={disabledDate}
                              size="large"
                              style={{width: "100%"}}
                              onChange={(date, dateString) => onChange(dateString)}
                              status={error?.message ? "error" : undefined}
                              value={!value ? undefined : moment(value)}
                              onBlur={onBlur}
                              ref={ref}
                            />
                          </Space>
                          <FormFieldError errorMessage={error?.message} />
                        </Space>
                      )}
                    />
                  </Col>
                </Row>
              </form>
            </div>
          </Space>
        </StyledModalContentWrapper>
      </Modal>
    </div>
  );
}
